import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { tokens } from "../../components/Theme";
import GetFertilizerStock from "../../api/stock/GetFertilizerStock";
import CreateFertilizerStock from "../../api/stock/CreateFertilizerStock";
import DeleteFertilizerStock from "../../api/stock/DeleteFertilizerStock";

export default function FertilizerStockTable({ appCropId }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [fertStocks, setFertStocks] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (appCropId) {
        try {
          const res = await GetFertilizerStock({ appCropId });
          setFertStocks(res);
        } catch (err) {
          console.error("Error fetching fertilizer stock:", err);
        }
      }
    };
    fetchData();
  }, [appCropId]);

  const handleChange = (index, key, value) => {
    const updated = [...fertStocks];
    updated[index][key] = value;
    setFertStocks(updated);
  };

  const handleAdd = () => {
    const timestamp = Math.floor(Date.now() / 1000);
    setFertStocks([
      ...fertStocks,
      {
        appCropId,
        appFertStockId: "SA" + timestamp,
        fertType: "เคมี",
        commercialName: "",
        formulaN: 0,
        formulaP: 0,
        formulaK: 0,
        unitName: "ลิตร",
        stockAmount: 0,
        stockDate: "",
      },
    ]);
  };

  const openDeleteDialog = (id) => {
    setSelectedDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await DeleteFertilizerStock({ appFertStockId: selectedDeleteId });
      setFertStocks((prev) =>
        prev.filter((item) => item.appFertStockId !== selectedDeleteId)
      );
      setSnackbarSeverity("success");
      setSnackbarMessage("ลบรายการสำเร็จ");
    } catch (err) {
      setSnackbarSeverity("error");
      setSnackbarMessage("เกิดข้อผิดพลาดในการลบ");
    } finally {
      setDeleteDialogOpen(false);
      setOpenSnackbar(true);
    }
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleSave = async () => {
    const results = await CreateFertilizerStock(fertStocks);
    const hasError = results.some((r) => r.status !== 200);
    if (hasError) {
      setSnackbarSeverity("error");
      setSnackbarMessage("บางรายการบันทึกไม่สำเร็จ");
    } else {
      setSnackbarSeverity("success");
      setSnackbarMessage("บันทึกสำเร็จทั้งหมด");
    }
    setOpenSnackbar(true);
  };

  return (
    <Box p={2}>
      <Box
        width="100%"
        sx={{ backgroundColor: colors.greenAccent[300] }}
        display="flex"
        justifyContent="center"
      >
        <Typography
          p={isMobile ? 1 : 2}
          variant={isMobile ? "body1" : "h6"}
          fontWeight="bold"
          color="#fff"
        >
          สต็อกปุ๋ย
        </Typography>
      </Box>

      <Box maxWidth={isMobile ? "320px" : "none"}>
        <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>วันที่</TableCell>
                <TableCell>ชื่อปุ๋ย</TableCell>
                <TableCell>สูตร (N-P-K)</TableCell>
                <TableCell>จำนวน</TableCell>
                <TableCell align="center">ลบ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fertStocks.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      size="small"
                      value={row.stockDate}
                      onChange={(e) =>
                        handleChange(index, "stockDate", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      sx={{ minWidth: "100px" }}
                      size="small"
                      value={row.commercialName}
                      onChange={(e) =>
                        handleChange(index, "commercialName", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      {["formulaN", "formulaP", "formulaK"].map((key) => (
                        <TextField
                          key={key}
                          size="small"
                          type="number"
                          value={row[key]}
                          onChange={(e) =>
                            handleChange(index, key, e.target.value)
                          }
                          sx={{ width: 60 }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <TextField
                      sx={{ minWidth: "100px" }}
                      size="small"
                      type="number"
                      value={row.stockAmount}
                      onChange={(e) =>
                        handleChange(index, "stockAmount", e.target.value)
                      }
                      InputProps={{
                        endAdornment: (
                          <span style={{ marginLeft: 4 }}>{row.unitName}</span>
                        ),
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="error"
                      onClick={() => openDeleteDialog(row.appFertStockId)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    variant="outlined"
                  >
                    เพิ่มแถว
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box textAlign="center" mt={3}>
        <Button
          variant="contained"
          sx={{ backgroundColor: colors.greenAccent[400] }}
          onClick={handleSave}
        >
          บันทึกข้อมูล
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            justifyContent: "center",
            minWidth: "200px",
            textAlign: "center",
          },
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          <Typography>คุณต้องการลบรายการนี้ใช่หรือไม่?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>ยกเลิก</Button>
          <Button color="error" onClick={confirmDelete}>
            ลบ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
